import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useWsNavigate } from "../hooks/useWsNavigate";
import PageHeader from "../components/layout/PageHeader";
import { promptOptimizerAPI, integrationAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import AgentInfo from "../components/prompt-optimizer/AgentInfo";
import PromptDiffViewer from "../components/prompt-optimizer/PromptDiffViewer";
import { Edit3, Save, X, Copy, Check, GitMerge, XCircle, BarChart3, Upload } from "lucide-react";

const PromptOptimizationDiffViewer = () => {
  const [optimization, setOptimization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editedInstructions, setEditedInstructions] = useState("");
  const [saving, setSaving] = useState(false);
  const [merging, setMerging] = useState(false);
  const [commitMessage, setCommitMessage] = useState("");
  const [copied, setCopied] = useState(false);
  const [showDiffOnly, setShowDiffOnly] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const { id } = useParams();
  const navigate = useWsNavigate();
  const location = useLocation();
  const { currentWorkspace } = useAuth();

  useEffect(() => {
    if (currentWorkspace?.id && id) fetchOptimization();
  }, [id, currentWorkspace?.id]);

  const fetchOptimization = async () => {
    try {
      const data = await promptOptimizerAPI.getOptimization(id, currentWorkspace.id);
      setOptimization(data);
      setEditedInstructions(data.new_instructions || "");
    } catch (error) {
      console.error("Error fetching optimization:", error);
      navigate("/prompt-optimizer");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await promptOptimizerAPI.updateOptimization(id, editedInstructions, currentWorkspace.id);
      setOptimization((prev) => ({ ...prev, new_instructions: editedInstructions }));
      setEditing(false);
    } catch (error) {
      console.error("Error saving edit:", error);
      alert("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  // "Sync with Test Agent & Copy" — makes this the current instruction on the
  // in-app (test) agent and copies the prompt to the clipboard.
  const handleMerge = async () => {
    setMerging(true);
    try {
      await navigator.clipboard.writeText(optimization?.new_instructions || "");
      await promptOptimizerAPI.mergeOptimization(id, commitMessage, currentWorkspace.id);
      navigate("/prompt-optimizer");
    } catch (error) {
      console.error("Error syncing with test agent:", error);
      alert("Failed to sync with the test agent");
    } finally {
      setMerging(false);
    }
  };

  const handleReject = async () => {
    try {
      await promptOptimizerAPI.rejectOptimization(id, currentWorkspace.id);
      navigate("/prompt-optimizer");
    } catch (error) {
      console.error("Error rejecting:", error);
    }
  };

  const handleCopy = () => {
    const text = optimization?.new_instructions || "";
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const vendorSyncTypes = ["external_vapi", "external_retell"];
  const integrationTypeName = (type) => {
    if (type === "external_vapi") return "Vapi";
    if (type === "external_retell") return "Retell";
    return null;
  };

  const handleSyncToVendor = async () => {
    setSyncing(true);
    setSyncError(null);
    try {
      // Merge locally first only if not already merged (writes agent.instructions),
      // then push that to the vendor. Lets an already-merged optimization still sync.
      if (optimization.status === "optimized") {
        await promptOptimizerAPI.mergeOptimization(
          id,
          commitMessage || "Merged & synced",
          currentWorkspace.id
        );
      }
      await integrationAPI.pushInstructions(
        currentWorkspace.id,
        optimization.agent.id,
        optimization.id
      );
      navigate("/prompt-optimizer");
    } catch (error) {
      console.error("Error syncing to vendor:", error);
      const msg = error?.response?.data?.error || "Sync failed";
      setSyncError(msg);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!optimization) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-red-500">Failed to load optimization</div>
      </div>
    );
  }

  const oldValue = optimization.last_instructions || "";
  const newValue = editing
    ? editedInstructions
    : typeof optimization.new_instructions === "string"
    ? optimization.new_instructions
    : optimization.new_instructions?.llm_full_suggestion || "";

  const isReset = optimization.source_type === "reset";
  const canMerge = optimization.status === "optimized";
  const canEdit = optimization.status === "optimized";
  const isReadOnly = !["optimized", "draft"].includes(optimization.status);

  const hasRewrite = typeof newValue === "string" && newValue.trim().length > 0;
  if (!hasRewrite) {
    return (
      <div className="px-4">
        <PageHeader
          title="Optimization"
          description="Review and apply the optimized prompt."
          breadcrumbs={[{ label: "Prompt Optimizer", path: "/prompt-optimizer" }]}
        />
        <div className="mt-6 max-w-xl mx-auto bg-card border border-border rounded-lg p-8 text-center">
          <p className="text-sm font-semibold text-foreground mb-1">No rewrite generated yet</p>
          <p className="text-sm text-muted-foreground mb-4">
            This optimization is still in the “{optimization.status}” stage — a rewritten
            prompt hasn’t been generated. Continue it from the optimizer.
          </p>
          <button
            onClick={() => navigate("/prompt-optimizer")}
            className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90"
          >
            Back to Optimizer
          </button>
        </div>
      </div>
    );
  }

  const gepa = optimization.gepa_metadata || {};

  return (
    <div className="px-4">
      <PageHeader
        title="Optimization Diff"
        description="Review and apply the optimized prompt."
        breadcrumbs={[
          { label: "Prompt Optimizer", path: "/prompt-optimizer" },
          { label: `v${optimization.version || "?"}` },
        ]}
      />

      <div className="mt-2">
        <AgentInfo
          agent={optimization.agent}
          updatedAt={optimization.updated_at}
        />

        {/* GEPA Metadata */}
        {(gepa.seed_val_score !== undefined || gepa.best_val_score !== undefined) && (
          <div className="mt-4 mb-4 p-3 bg-muted/50 rounded-lg border border-border">
            <div className="flex items-center space-x-2 mb-2">
              <BarChart3 className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">GEPA Results</span>
            </div>
            <div className="grid grid-cols-4 gap-4 text-sm">
              {gepa.seed_val_score !== undefined && gepa.seed_val_score !== null && (
                <div>
                  <span className="text-muted-foreground">Seed Score</span>
                  <p className="font-medium">{(gepa.seed_val_score * 100).toFixed(0)}%</p>
                </div>
              )}
              {gepa.best_val_score !== undefined && gepa.best_val_score !== null && (
                <div>
                  <span className="text-muted-foreground">Best Score</span>
                  <p className="font-medium text-green-600">{(gepa.best_val_score * 100).toFixed(0)}%</p>
                </div>
              )}
              {gepa.total_evaluations && (
                <div>
                  <span className="text-muted-foreground">Evaluations</span>
                  <p className="font-medium">{gepa.total_evaluations}</p>
                </div>
              )}
              {gepa.num_candidates && (
                <div>
                  <span className="text-muted-foreground">Candidates</span>
                  <p className="font-medium">{gepa.num_candidates}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Summary */}
        {optimization.summary && (
          <div className="mb-4 text-sm text-muted-foreground bg-blue-50 border border-blue-200 rounded-lg p-3">
            {optimization.summary}
          </div>
        )}

        {/* Toolbar — toggle (left) + actions (right) in one row */}
        <div className="flex items-center justify-between mb-2">
          {!editing ? (
            <label className="flex items-center cursor-pointer select-none">
              <span className="mr-2 text-sm text-muted-foreground">Show only changes</span>
              <div className="relative">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={showDiffOnly}
                  onChange={(e) => setShowDiffOnly(e.target.checked)}
                />
                <div
                  className={`block w-10 h-6 rounded-full transition-colors ${
                    showDiffOnly ? "bg-primary" : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`absolute left-1 top-1 bg-card w-4 h-4 rounded-full transition-transform ${
                      showDiffOnly ? "translate-x-4" : ""
                    }`}
                  />
                </div>
              </div>
            </label>
          ) : (
            <span />
          )}
          <div className="flex items-center space-x-2">
            {canEdit && !editing && (
              <button
                onClick={() => setEditing(true)}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm text-muted-foreground bg-muted rounded-md hover:bg-gray-200"
              >
                <Edit3 className="w-3.5 h-3.5" />
                <span>Edit</span>
              </button>
            )}
            <button
              onClick={handleCopy}
              className="flex items-center space-x-1 px-3 py-1.5 text-sm text-muted-foreground bg-muted rounded-md hover:bg-gray-200"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 text-green-500" />
                  <span>Copied</span>
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" />
                  <span>Copy Optimized</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Diff or Edit view */}
        {editing ? (
          <div className="border rounded-lg p-4">
            <textarea
              value={editedInstructions}
              onChange={(e) => setEditedInstructions(e.target.value)}
              className="w-full h-96 font-mono text-sm p-3 border border-input rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            />
            <div className="flex justify-end space-x-2 mt-3">
              <button
                onClick={() => {
                  setEditing(false);
                  setEditedInstructions(optimization.new_instructions || "");
                }}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-muted rounded-md hover:bg-gray-200"
              >
                <X className="w-3.5 h-3.5" />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
              >
                <Save className="w-3.5 h-3.5" />
                <span>{saving ? "Saving..." : "Save"}</span>
              </button>
            </div>
          </div>
        ) : (
          <PromptDiffViewer
            oldValue={oldValue}
            newValue={newValue}
            showDiffOnly={showDiffOnly}
            leftTitle={isReset ? "Was current" : "Current Instructions"}
            rightTitle={isReset ? "Reverted to (now current)" : "Optimized Instructions"}
          />
        )}

        {/* Actions */}
        <div className="mt-6">
          {canMerge && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-1">
                Version label <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={commitMessage}
                onChange={(e) => setCommitMessage(e.target.value)}
                placeholder="e.g. Warmer greeting + confirm resolution"
                className="w-full px-3 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
              />
            </div>
          )}
          {syncError && (
            <div className="mb-3 px-3 py-2 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
              {syncError}
            </div>
          )}
          {!canMerge &&
            optimization.status === "merged" &&
            vendorSyncTypes.includes(optimization.agent?.integration_type) && (
              <div className="mb-3 px-3 py-2 text-sm text-muted-foreground bg-muted/50 border border-border rounded-md">
                Current on the test agent. Sync with{" "}
                {integrationTypeName(optimization.agent?.integration_type)} to apply it on the live
                agent.
              </div>
            )}
          <div className="flex justify-end space-x-2">
            <button
              onClick={() => navigate("/prompt-optimizer")}
              className="px-4 py-2 text-sm bg-muted text-foreground rounded-md hover:bg-muted/80"
            >
              Back
            </button>
            {canMerge && (
              <>
                <button
                  onClick={handleReject}
                  className="flex items-center space-x-1 px-4 py-2 text-sm text-destructive bg-destructive/10 rounded-md hover:bg-destructive/20"
                >
                  <XCircle className="w-4 h-4" />
                  <span>Reject</span>
                </button>
                <button
                  onClick={handleMerge}
                  disabled={merging || syncing}
                  className={`flex items-center space-x-1 px-4 py-2 text-sm text-white bg-primary rounded-md ${
                    merging || syncing ? "opacity-50 cursor-not-allowed" : "hover:bg-primary/90"
                  }`}
                  title="Make this the current instruction on the in-app test agent and copy it"
                >
                  <GitMerge className="w-4 h-4" />
                  <span>{merging ? "Syncing..." : "Sync with Test Agent & Copy"}</span>
                </button>
              </>
            )}
            {/* Vendor push — available while optimized AND after a local merge,
                so a vendor agent is never left applied-locally-but-not-on-vendor. */}
            {vendorSyncTypes.includes(optimization.agent?.integration_type) &&
              (canMerge || optimization.status === "merged") && (
                <button
                  onClick={handleSyncToVendor}
                  disabled={syncing || merging}
                  className={`flex items-center space-x-1 px-4 py-2 text-sm text-white bg-indigo-600 rounded-md ${
                    syncing || merging ? "opacity-50 cursor-not-allowed" : "hover:bg-indigo-700"
                  }`}
                  title={`Merge locally and push the prompt to ${integrationTypeName(optimization.agent?.integration_type)}`}
                >
                  <Upload className="w-4 h-4" />
                  <span>
                    {syncing
                      ? "Syncing..."
                      : `Sync with ${integrationTypeName(optimization.agent?.integration_type)}`}
                  </span>
                </button>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptOptimizationDiffViewer;
